// MembershipPackages.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { Card, Table, Form, Button, Modal, Badge, Row, Col, Spinner, Alert, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { 
  getAllMembershipPackages, 
  addMembershipPackage, 
  updateMembershipPackage, 
  deleteMembershipPackage
} from '../services/membershipPackageService';

const MembershipPackages = () => {
  // State tanımlamaları
  const [packages, setPackages] = useState([]);
  const [filteredPackages, setFilteredPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Filtreleme
  const [searchName, setSearchName] = useState('');

  // Yeni ekleme
  const [showAddModal, setShowAddModal] = useState(false);
  const [newPackage, setNewPackage] = useState({
    name: '',
    description: '',
    price: '',
    durationInDays: 30
  });

  // Düzenleme
  const [showEditModal, setShowEditModal] = useState(false);
  const [editPackage, setEditPackage] = useState({
    id: '',
    name: '',
    description: '',
    price: '',
    durationInDays: 30
  });

  // Silme
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);

  // Veri yükleme fonksiyonu
  const fetchPackages = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllMembershipPackages();
      if (response.Status) {
        setPackages(response.Data.Items || []);
        setFilteredPackages(response.Data.Items || []);
      } else {
        toast.error(response.Message || 'Üyelik paketleri yüklenemedi.');
      }
    } catch (error) {
      console.error('Üyelik paketleri yükleme hatası:', error);
      toast.error('Üyelik paketleri yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  // Filtreleme fonksiyonu
  useEffect(() => {
    let filtered = packages;

    if (searchName) {
      filtered = filtered.filter(pkg => 
        pkg.name.toLowerCase().includes(searchName.toLowerCase()) ||
        pkg.description?.toLowerCase().includes(searchName.toLowerCase())
      );
    }

    setFilteredPackages(filtered);
  }, [packages, searchName]);

  // Ekleme fonksiyonu
  const handleAdd = async () => {
    if (!newPackage.name.trim()) {
      toast.warning('Lütfen paket adını girin.');
      return;
    }

    const price = parseFloat(newPackage.price);
    if (isNaN(price) || price < 0) {
      toast.warning('Geçerli bir fiyat girin.');
      return;
    }

    if (newPackage.durationInDays < 1) {
      toast.warning('Süre en az 1 gün olmalıdır.');
      return;
    }

    try {
      setProcessing(true);
      const packageData = {
        ...newPackage,
        price: price
      };
      const response = await addMembershipPackage(packageData);
      
      if (response.Status) {
        toast.success(response.Message || 'Üyelik paketi başarıyla eklendi.');
        setShowAddModal(false);
        setNewPackage({
          name: '',
          description: '',
          price: '',
          durationInDays: 30
        });
        await fetchPackages();
      } else {
        toast.error(response.Message || 'Üyelik paketi eklenirken hata oluştu.');
      }
    } catch (error) {
      console.error('Ekleme hatası:', error);
      toast.error('Üyelik paketi eklenirken hata oluştu.');
    } finally {
      setProcessing(false);
    }
  };

  // Güncelleme fonksiyonu
  const handleUpdate = async () => {
    if (!editPackage.name.trim()) {
      toast.warning('Lütfen paket adını girin.');
      return;
    }

    const price = parseFloat(editPackage.price);
    if (isNaN(price) || price < 0) {
      toast.warning('Geçerli bir fiyat girin.');
      return;
    }

    if (editPackage.durationInDays < 1) {
      toast.warning('Süre en az 1 gün olmalıdır.');
      return;
    }

    try {
      setProcessing(true);
      const packageData = {
        ...editPackage,
        price: price
      };
      const response = await updateMembershipPackage(packageData);
      
      if (response.Status) {
        toast.success(response.Message || 'Üyelik paketi başarıyla güncellendi.');
        setShowEditModal(false);
        await fetchPackages();
      } else {
        toast.error(response.Message || 'Üyelik paketi güncellenirken hata oluştu.');
      }
    } catch (error) {
      console.error('Güncelleme hatası:', error);
      toast.error('Üyelik paketi güncellenirken hata oluştu.');
    } finally {
      setProcessing(false);
    }
  };

  // Silme fonksiyonu
  const handleDelete = async () => {
    if (!deleteCandidate) return;

    try {
      setProcessing(true);
      const response = await deleteMembershipPackage(deleteCandidate.id);
      
      if (response.Status) {
        toast.success(response.Message || 'Üyelik paketi başarıyla silindi.');
        setShowDeleteModal(false);
        setDeleteCandidate(null);
        await fetchPackages();
      } else {
        toast.error(response.Message || 'Üyelik paketi silinirken hata oluştu.');
      }
    } catch (error) {
      console.error('Silme hatası:', error);
      toast.error('Üyelik paketi silinirken hata oluştu.');
    } finally {
      setProcessing(false);
    }
  };

  const handleStartEdit = (pkg) => {
    setEditPackage({
      id: pkg.id,
      name: pkg.name,
      description: pkg.description || '',
      price: pkg.price.toString(),
      durationInDays: pkg.durationInDays
    });
    setShowEditModal(true);
  };

  const resetFilters = () => {
    setSearchName('');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(price);
  };

  return (
    <div className="container-fluid p-4">
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0">Üyelik Paketleri</h4>
            <Button 
              variant="primary" 
              onClick={() => setShowAddModal(true)}
              disabled={processing}
            >
              + Yeni Paket Ekle
            </Button>
          </div>
        </Card.Header>
        
        <Card.Body>
          {/* Filtreleme Alanı */}
          <Row className="mb-4">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Paket Adı / Açıklama Ara</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Paket adı veya açıklama..."
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>&nbsp;</Form.Label>
                <div className="d-grid">
                  <Button variant="outline-secondary" onClick={resetFilters}>
                    Filtreleri Temizle
                  </Button>
                </div>
              </Form.Group>
            </Col>
          </Row>

          {/* İstatistikler */}
          <Row className="mb-3">
            <Col>
              <Alert variant="info" className="mb-0">
                <strong>Toplam:</strong> {packages.length} paket | 
                <strong> Görüntülenen:</strong> {filteredPackages.length} paket
              </Alert>
            </Col>
          </Row>

          {/* Tablo */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </Spinner>
            </div>
          ) : (
            <Table bordered hover responsive>
              <thead className="table-dark">
                <tr>
                  <th style={{ width: '25%' }}>Paket Adı</th>
                  <th style={{ width: '35%' }}>Açıklama</th>
                  <th style={{ width: '15%' }}>Fiyat</th>
                  <th style={{ width: '10%' }}>Süre</th>
                  <th style={{ width: '15%' }}>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {filteredPackages.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center text-muted py-4">
                      Üyelik paketi bulunamadı.
                    </td>
                  </tr>
                ) : (
                  filteredPackages.map((pkg) => (
                    <tr key={pkg.id}>
                      <td>
                        <strong>{pkg.name}</strong>
                      </td>
                      <td>
                        <small className="text-muted">
                          {pkg.description ? 
                            (pkg.description.length > 100 ? 
                              pkg.description.substring(0, 100) + '...' : 
                              pkg.description) : 
                            '-'}
                        </small>
                      </td>
                      <td>
                        <Badge bg="success" className="fs-6">
                          {formatPrice(pkg.price)}
                        </Badge>
                      </td>
                      <td>
                        {pkg.durationInDays} gün
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            size="sm"
                            variant="warning"
                            onClick={() => handleStartEdit(pkg)}
                            disabled={processing}
                            title="Düzenle"
                          >
                            Düzenle
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => {
                              setDeleteCandidate(pkg);
                              setShowDeleteModal(true);
                            }}
                            disabled={processing}
                            title="Sil"
                          >
                            Sil
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Ekleme Modalı */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Yeni Üyelik Paketi Ekle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Paket Adı *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Örn: Premium Paket"
                value={newPackage.name}
                onChange={(e) => setNewPackage({...newPackage, name: e.target.value})}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Açıklama</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Paket açıklaması..."
                value={newPackage.description}
                onChange={(e) => setNewPackage({...newPackage, description: e.target.value})}
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Fiyat (TL) *</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>₺</InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="0.00"
                      value={newPackage.price}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Sadece sayı ve nokta karakterlerine izin ver
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          setNewPackage({...newPackage, price: value});
                        }
                      }}
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Süre (Gün) *</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={newPackage.durationInDays}
                    onChange={(e) => setNewPackage({...newPackage, durationInDays: parseInt(e.target.value) || 1})}
                  />
                  <Form.Text className="text-muted">
                    30 gün = 1 ay, 365 gün = 1 yıl
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            İptal
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAdd}
            disabled={processing}
          >
            {processing ? <Spinner size="sm" className="me-2" /> : ''}
            Ekle
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Düzenleme Modalı */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Üyelik Paketi Düzenle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Paket Adı *</Form.Label>
              <Form.Control
                type="text"
                value={editPackage.name}
                onChange={(e) => setEditPackage({...editPackage, name: e.target.value})}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Açıklama</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={editPackage.description}
                onChange={(e) => setEditPackage({...editPackage, description: e.target.value})}
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Fiyat (TL) *</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>₺</InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="0.00"
                      value={editPackage.price}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Sadece sayı ve nokta karakterlerine izin ver
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          setEditPackage({...editPackage, price: value});
                        }
                      }}
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Süre (Gün) *</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={editPackage.durationInDays}
                    onChange={(e) => setEditPackage({...editPackage, durationInDays: parseInt(e.target.value) || 1})}
                  />
                  <Form.Text className="text-muted">
                    30 gün = 1 ay, 365 gün = 1 yıl
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            İptal
          </Button>
          <Button 
            variant="primary" 
            onClick={handleUpdate}
            disabled={processing}
          >
            {processing ? <Spinner size="sm" className="me-2" /> : ''}
            Güncelle
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Silme Modalı */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Üyelik Paketi Sil</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p><strong>"{deleteCandidate?.name}"</strong> paketini silmek istediğinizden emin misiniz?</p>
          <Alert variant="warning">
            <small>Bu işlem geri alınamaz. Bu paketi kullanan mevcut üyelikler etkilenebilir.</small>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            İptal
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDelete}
            disabled={processing}
          >
            {processing ? <Spinner size="sm" className="me-2" /> : ''}
            Sil
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MembershipPackages;